<template>
  <!-- Edit a client from the super admin client list: details, trial and modules in a side panel (US 9436) -->
  <b-modal ref="panel"
           modal-class="us-drawer"
           hide-header
           hide-footer
           @hidden="reset"
  >
    <div v-if="client" class="us-drawer__inner" qid="edit-client-panel">
      <header class="us-drawer__head">
        <img :src="avatarUrl" class="adm-avatar adm-avatar--lg" alt="">
        <div class="us-drawer__who">
          <h2 class="us-drawer__name" qid="edit-client-name">{{ client.name }}</h2>
          <div class="us-drawer__meta">
            {{ $tc('admin_page.clients.users_count', client.users_count || 0, { count: client.users_count || 0 }) }}
            · {{ $tc('admin_page.clients.partitions_count', client.partitions_count || 0, { count: client.partitions_count || 0 }) }}
          </div>
        </div>
        <button type="button"
                class="us-icon-btn us-drawer__close"
                :aria-label="$t('administration.clients.create.cancel')"
                @click="close">
          <font-awesome-icon icon="times"/>
        </button>
      </header>

      <b-form class="us-drawer__body" qid="edit-client-form" @submit.prevent="save">
        <b-alert v-if="error"
                 show
                 variant="danger"
                 qid="edit-client-error">{{ error }}</b-alert>

        <section class="us-drawer__section">
          <h3 class="us-drawer__label">{{ $t('admin_page.clients.edit.section_details') }}</h3>
          <div class="adm-fields">
            <label class="adm-field adm-field--wide">
              <span class="us-field-label">{{ $t('administration.clients.create.name_label') }}</span>
              <b-form-input v-model="form.name" required qid="edit-client-form-name"/>
            </label>
            <label class="adm-field">
              <span class="us-field-label">{{ $t('administration.clients.create.cvr_label') }}</span>
              <b-form-input v-model="form.cvr" qid="edit-client-form-cvr"/>
            </label>
            <label class="adm-field">
              <span class="us-field-label">{{ $t('administration.partitions.create.reference_label') }}</span>
              <b-form-input v-model="form.reference_number" qid="edit-client-form-reference"/>
            </label>
            <label class="adm-field adm-field--wide">
              <span class="us-field-label">{{ $t('administration.clients.create.street_label') }}</span>
              <b-form-input v-model="form.street" qid="edit-client-form-street"/>
            </label>
            <label class="adm-field">
              <span class="us-field-label">{{ $t('administration.clients.create.postcode_label') }}</span>
              <b-form-input v-model="form.postcode" qid="edit-client-form-postcode"/>
            </label>
            <label class="adm-field">
              <span class="us-field-label">{{ $t('administration.clients.create.city_label') }}</span>
              <b-form-input v-model="form.city" qid="edit-client-form-city"/>
            </label>
            <label class="adm-field adm-field--wide">
              <span class="us-field-label">{{ $t('administration.clients.create.country_label') }}</span>
              <b-form-input v-model="form.country" qid="edit-client-form-country"/>
            </label>
          </div>
        </section>

        <section class="us-drawer__section">
          <h3 class="us-drawer__label">{{ $t('admin_page.clients.edit.section_trial') }}</h3>
          <div class="adm-trial-row">
            <datepicker v-model="form.trial_ends_at"
                        class="date-picker adm-trial-row__picker"
                        :clear-button="true"
                        :placeholder="$t('administration.clients.create.trial_label')"
                        qid="edit-client-form-trial"
            ></datepicker>
            <TrialPill :client="{ trial_ends_at: form.trial_ends_at, paying: client.paying }"/>
          </div>
          <div class="us-note us-note--info">
            <font-awesome-icon icon="info-circle"/>
            <span>{{ $t('admin_page.clients.edit.trial_hint') }}</span>
          </div>
        </section>

        <section class="us-drawer__section">
          <div class="adm-modules-head">
            <h3 class="us-drawer__label">{{ $t('admin_page.clients.edit.section_modules') }}</h3>
            <span class="v2-tag v2-tag--grey">{{ moduleRows.filter(isOn).length }}/{{ moduleRows.length }}</span>
          </div>
          <div v-for="group in groups" :key="group.key" class="adm-module-group">
            <div class="adm-module-group__title">{{ $t('admin_page.modules.groups.' + group.key) }}</div>
            <label v-for="module in group.modules"
                   :key="module.code"
                   class="adm-module"
                   :class="{ 'is-on': isOn(module) }"
                   :qid="'edit-client-module-' + module.code"
            >
              <span class="adm-module__icon"><font-awesome-icon :icon="module.icon" fixed-width/></span>
              <span class="adm-module__name">{{ $t(module.label) }}</span>
              <button type="button"
                      class="us-toggle"
                      :class="{ 'is-on': isOn(module) }"
                      role="switch"
                      :aria-checked="isOn(module) ? 'true' : 'false'"
                      :aria-label="$t(module.label)"
                      @click="toggle(module)"
              ></button>
            </label>
          </div>
        </section>

        <div class="us-drawer__foot">
          <span class="us-grow"></span>
          <b-button variant="secondary" qid="edit-client-cancel-button" @click="close">
            {{ $t('administration.clients.create.cancel') }}
          </b-button>
          <b-button type="submit"
                    variant="success"
                    :disabled="saving"
                    qid="edit-client-submit-button"
          >
            <font-awesome-icon v-if="saving" icon="spinner" class="fa-spin mr-1"/>
            {{ $t('administration.clients.create.submit') }}
          </b-button>
        </div>
      </b-form>
    </div>
  </b-modal>
</template>

<script>
import piincHttp from '@/util/configureAxios.js';
import Datepicker from 'vuejs-datepicker';
import {userAvatarUrl} from '@/util/userAvatar.js';
import {MODULES, MODULE_GROUPS} from '@/util/adminModules.js';
import TrialPill from '@/components/administration/TrialPill.vue';

const FIELDS = ['name', 'cvr', 'street', 'city', 'postcode', 'country', 'reference_number', 'trial_ends_at'];

export default {
  name: 'ClientEditPanel',
  components: {
    Datepicker,
    TrialPill
  },
  props: {
    // Modules from GET modules: id, name and code
    modules: {
      type: Array,
      default: () => []
    }
  },
  data() {
    return {
      client: null,
      form: {},
      selected: [],
      error: '',
      saving: false
    }
  },
  computed: {
    avatarUrl() {
      return userAvatarUrl(this.client);
    },
    // Our module list with the database id, for the ones that exist
    moduleRows() {
      return MODULES
        .map(module => Object.assign({}, module, { id: (this.modules.find(m => m.code === module.code) || {}).id }))
        .filter(module => module.id);
    },
    groups() {
      return MODULE_GROUPS
        .map(key => ({ key: key, modules: this.moduleRows.filter(module => module.group === key) }))
        .filter(group => group.modules.length);
    }
  },
  methods: {
    open(client) {
      this.client = client;
      this.form = _.pick(client, FIELDS);
      // A client can have the same module more than once in client_modules
      this.selected = _.uniq((client.activeModules || []).map(module => module.id));
      this.error = '';
      this.$refs.panel.show();
    },
    close() {
      this.$refs.panel.hide();
    },
    reset() {
      this.client = null;
    },
    isOn(module) {
      return this.selected.indexOf(module.id) !== -1;
    },
    toggle(module) {
      this.selected = this.isOn(module)
        ? this.selected.filter(id => id !== module.id)
        : this.selected.concat(module.id);
    },
    save() {
      let self = this;
      this.saving = true;
      this.error = '';
      let data = Object.assign({}, this.form, { modules: _.uniq(this.selected) });
      piincHttp.put('clients/' + this.client.slug, data).then(function() {
        self.saving = false;
        self.$toastr('success', self.$t('administration.clients.update.success'));
        self.$emit('saved');
        self.close();
      }, function(error) {
        self.saving = false;
        self.error = (error.data && error.data.message) || self.$t('admin_page.error');
      });
    }
  }
}
</script>
